/* eslint-disable no-nested-ternary */
import * as React from 'react'
import PropTypes from 'prop-types'
import { ariaProps, dataProps, onProps, useUniqueId } from '@ds/react-utils'
import { MotionVariant } from '@ds/motion'
import { consoleWarn, deprecatedPropMessage } from '../../logging'
import { CustomPropTypes } from '../../support'
import { useDsUiDataProps, useThemeStyles } from '../../theming'
import type {
  AriaAttributes,
  EventListenerPropsOmittingOnChange,
  InputOnChangeHandler,
  LabelForwardRef,
} from '../../types'
import { InputDescription } from '../InputDescription'
import type { TextEllipsisProps } from '../TextEllipsis'
import styles from './styles'
import { useFormContext } from '../FormContext/FormContext'

export interface RadioButtonProps
  extends EventListenerPropsOmittingOnChange<HTMLLabelElement>,
    AriaAttributes {
  /**
   * Accepts aria attributes.
   */
  'aria-.*'?: string
  /**
   * Indicates that the RadioButton is selected.
   */
  checked?: boolean
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * Applies a 'description' treatment, displaying the provided text.
   */
  description?: string
  /**
   * Disables the RadioButton.
   */
  disabled?: boolean
  /**
   * Applies an 'error' treatment, displaying the provided text.
   */
  error?: string
  /**
   * A React ref to assign to the HTML label element.
   */
  forwardedRef?: LabelForwardRef
  /**
   * The identifier used to group a RadioButton set.
   */
  groupIdentifier?: string
  /**
   * Hide the RadioButton label while allowing assistive devices to identify the control.
   */
  hideLabel?: boolean
  /**
   * A unique identifier for the input element.
   */
  id?: string
  /**
   * Applies an 'invalid' visual treatment. Use the `error` prop instead.
   * @deprecated
   */
  invalid?: boolean
  /**
   * The label for the RadioButton. If truncation is required, use the `TextEllipsis` component.
   */
  label: string | React.ReactElement<TextEllipsisProps>
  /**
   * Accepts attributes matching the pattern on[A-Z].* in order to register event handlers.
   */
  'on[A-Z].*'?: React.EventHandler<React.SyntheticEvent<HTMLInputElement>>
  /**
   * The function to call when a 'change' event is fired.
   */
  onChange?: InputOnChangeHandler
  /**
   * @deprecated - use `required` in RadioButtonGroup instead.
   */
  required?: boolean
  /**
   * The value of the RadioButton.
   */
  value?: string
}

/**
 * Radio Buttons let users choose a single selection among a group of mutually exclusive choices.
 */
export function RadioButton({
  checked = false,
  'data-qa': dataQa,
  description,
  disabled = false,
  error,
  forwardedRef,
  groupIdentifier,
  hideLabel = false,
  id,
  invalid = false,
  label,
  onChange,
  required,
  value,
  ...restProps
}: RadioButtonProps) {
  if (required) {
    consoleWarn(
      `The "required" prop has been deprecated. Please use the "required" prop in the RadioButtonGroup. RadioButtons should only be used inside a RadioButtonGroup.`,
    )
  }

  if (invalid) {
    deprecatedPropMessage({
      component: 'RadioButton',
      prop: 'invalid',
      newProp: 'error',
    })
  }

  const dsUiDataProps = useDsUiDataProps(RadioButton)

  const formContext = useFormContext()

  const isInvalid = (invalid || error) && !restProps['aria-invalid']
  const sx = useThemeStyles(styles, {
    disabled,
    description,
    error,
    hideLabel,
    isInvalid: !!isInvalid,
  })

  const generatedRadioButtonId = useUniqueId('short')
  const radioButtonId = id || generatedRadioButtonId

  const errorId = useUniqueId('short')
  const descriptionId = useUniqueId('short')

  const descriptionNode = description && (
    <div css={sx.default.description}>
      <InputDescription
        id={descriptionId}
        kind="helper"
        disabled={disabled}
        data-qa={dataQa && `${dataQa}-description`}
      >
        {description}
      </InputDescription>
    </div>
  )

  const errorNode = error && (
    <div css={sx.default.error}>
      <InputDescription
        id={errorId}
        kind="error"
        disabled={disabled}
        data-qa={dataQa && `${dataQa}-error`}
      >
        {error}
      </InputDescription>
    </div>
  )

  const ariaDescribedByValue = [
    description && !error ? descriptionId : '',
    !description && error ? errorId : '',
    description && error ? `${descriptionId} ${errorId}` : '',
    restProps['aria-describedby'] || '',
  ]
    .join(' ')
    .trim()

  const additionalAttributes = {
    ...(ariaDescribedByValue && { 'aria-describedby': ariaDescribedByValue }),
    ...(invalid || error ? { 'aria-invalid': true } : {}),
  }

  return (
    <div css={sx.default.wrap} {...dsUiDataProps}>
      <MotionVariant
        css={sx.default.innerWrap}
        initial={false}
        animate={checked ? 'checked' : 'unchecked'}
        whileHover={
          disabled
            ? undefined
            : checked
              ? 'hoverWhenChecked'
              : 'hoverWhenUnchecked'
        }
        whileTap={
          disabled ? undefined : checked ? 'tapWhenChecked' : 'tapWhenUnchecked'
        }
      >
        <input
          {...additionalAttributes}
          {...ariaProps(restProps)}
          {...dataProps(restProps)}
          checked={checked}
          css={sx.default.input}
          data-qa={dataQa}
          disabled={disabled}
          id={radioButtonId}
          onChange={onChange || (() => undefined)} // Adding the logical OR here to account for failing proptypes in unit test
          name={groupIdentifier}
          required={formContext?.requiredAttribute ? required : undefined}
          type="radio"
          value={value}
        />
        <label
          {...onProps(restProps)}
          css={sx.default.label}
          data-qa={dataQa && `${dataQa}-label`}
          htmlFor={radioButtonId}
          ref={forwardedRef}
        >
          <span
            css={sx.default.labelText}
            data-qa={dataQa && `${dataQa}-label-text`}
          >
            {label}
          </span>
        </label>
        <span className="focusStyles" css={sx.default.focusStyles}>
          <MotionVariant
            as="span"
            css={sx.default.radioButtonBorder}
            className="radioButtonBorder"
            variants={sx.motionVariants.radioButtonBorder}
          >
            <MotionVariant
              as="span"
              css={sx.default.radioButtonHover}
              variants={sx.motionVariants.radioButtonHover}
            />
            <MotionVariant
              as="span"
              css={sx.default.radioButtonFill}
              className="radioButtonFill"
              variants={sx.motionVariants.radioButtonFill}
            />
          </MotionVariant>
        </span>
      </MotionVariant>
      {descriptionNode}
      {errorNode}
    </div>
  )
}

RadioButton.propTypes = {
  'aria-.*': PropTypes.string,
  checked: PropTypes.bool,
  'data-.*': PropTypes.string,
  description: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  forwardedRef: CustomPropTypes.ReactRef,
  groupIdentifier: PropTypes.string,
  hideLabel: PropTypes.bool,
  id: PropTypes.string,
  invalid: PropTypes.bool,
  label: PropTypes.node.isRequired,
  'on[A-Z].*': PropTypes.func,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  value: PropTypes.string,
}

RadioButton.displayName = 'RadioButton'
